import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import { estimateService } from '../services/estimateService';

const Estimates = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchEstimates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await estimateService.getEstimates();
      setEstimates(res.data || []);
      setError('');
    } catch {
      setError('Failed to load estimates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDelete = async (id) => {
    try {
      await estimateService.deleteEstimate(id);
      setEstimates(prev => prev.filter(e => e.id !== id));
      setDeleteConfirm(null);
      showSuccess('Estimate deleted.');
    } catch {
      showSuccess('Failed to delete estimate.');
    }
  };

  const calcTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + Math.abs(parseFloat(item.value || 0)), 0);
  };

  const formatValue = (value) => {
    return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-4 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Estimates</span>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <span className="text-sm text-gray-600 dark:text-gray-300">{user?.name}</span>
              <button
                onClick={logout}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {successMessage && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg text-sm">
            {successMessage}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">All Estimates</h1>
            <span className="text-sm text-gray-500 dark:text-gray-400">{estimates.length} estimate{estimates.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500">
              Loading...
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-16 text-red-500 text-sm">{error}</div>
          ) : estimates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
              <p className="text-sm">No estimates yet.</p>
              <p className="text-xs mt-1">Create estimates from a job's detail page.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {estimates.map((estimate) => {
                const total = calcTotal(estimate.items);
                const gross = estimate.items?.reduce((sum, item) => sum + parseFloat(item.value || 0), 0) || 0;
                return (
                  <div key={estimate.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => navigate(`/estimates/${estimate.id}`)}
                        className="text-base font-medium text-blue-600 dark:text-blue-400 hover:underline text-left truncate"
                      >
                        {estimate.title}
                      </button>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {estimate.job && (
                          <span>
                            Job:{' '}
                            <Link to={`/jobs/${estimate.job.id}`} className="text-blue-500 dark:text-blue-400 hover:underline">
                              {estimate.job.title}
                            </Link>
                            {estimate.job.site && (
                              <span className="ml-1">
                                &middot;{' '}
                                <Link to={`/sites/${estimate.job.site_id}`} className="hover:underline">
                                  {estimate.job.site.name}
                                </Link>
                              </span>
                            )}
                          </span>
                        )}
                        <span>{estimate.items?.length || 0} line {estimate.items?.length === 1 ? 'item' : 'items'}</span>
                        <span>{formatDate(estimate.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="text-base font-semibold text-gray-900 dark:text-white">
                        {formatValue(total)}
                        {gross !== total && (
                          <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">({formatValue(gross)})</span>
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/estimates/${estimate.id}`)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                        >
                          View
                        </button>
                        {deleteConfirm === estimate.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                            <button
                              onClick={() => handleDelete(estimate.id)}
                              className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors"
                            >Yes</button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            >No</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(estimate.id)}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Estimates;
